import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export enum SubscriptionTier {
  FREE = 'free',
  PREMIUM = 'premium'
}

export interface SubscriptionLimits {
  dailyMessageLimit: number;
  bookmarkLimit: number;
  historyDays: number;
  canExport: boolean;
  canUseAdvancedSearch: boolean;
  hasPrioritySupport: boolean;
}

const TIER_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  [SubscriptionTier.FREE]: {
    dailyMessageLimit: 3,
    bookmarkLimit: 1,
    historyDays: 7,
    canExport: false,
    canUseAdvancedSearch: false,
    hasPrioritySupport: false,
  },
  [SubscriptionTier.PREMIUM]: {
    dailyMessageLimit: Infinity,
    bookmarkLimit: Infinity,
    historyDays: Infinity,
    canExport: true,
    canUseAdvancedSearch: true,
    hasPrioritySupport: true,
  },
};

interface SubscriptionContextType {
  tier: SubscriptionTier;
  limits: SubscriptionLimits;
  isLoading: boolean;
  dailyMessageCount: number;
  subscriptionError: string | null;
  incrementMessageCount: () => void;
  resetMessageCount: () => void;
  refreshSubscription: () => Promise<void>;
  checkCanPerformAction: (action: 'message' | 'bookmark' | 'search' | 'export') => { 
    allowed: boolean; 
    reason?: string;
  };
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>(SubscriptionTier.FREE);
  const [isLoading, setIsLoading] = useState(true);
  const [dailyMessageCount, setDailyMessageCount] = useState(0);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  // Make refreshSubscription a memoized callback so it doesn't cause effect dependencies to change
  const refreshSubscription = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      console.log('Manually refreshing subscription status...');
      setIsLoading(true);
      setSubscriptionError(null);
      
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const data = userDoc.data();
      
      if (data) {
        console.log('Refreshed user data:', data);
        setTier(data.subscriptionTier || SubscriptionTier.FREE);
        setDailyMessageCount(data.dailyMessageCount || 0);
      } else {
        console.log('User document not found or empty');
        // If no document exists, it may be a new user - create default fields
        // This would normally be handled elsewhere but this is a fallback
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setSubscriptionError('Failed to refresh subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Listen to subscription changes with error handling and automatic retry
  useEffect(() => {
    if (!currentUser) {
      setTier(SubscriptionTier.FREE);
      setIsLoading(false);
      return;
    }

    console.log('Setting up subscription listener for user:', currentUser.uid);
    setIsLoading(true);
    setSubscriptionError(null);

    // Immediately refresh subscription data when user logs in
    refreshSubscription()
      .then(() => console.log('Initial subscription refresh completed'))
      .catch(err => console.error('Error during initial subscription refresh:', err));

    let retryCount = 0;
    const maxRetries = 3;
    let unsubscribe: (() => void) | null = null;

    const setupListener = () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        
        unsubscribe = onSnapshot(userRef, (doc) => {
          const data = doc.data();
          console.log('Subscription data updated:', data);
          
          if (data) {
            setTier(data.subscriptionTier || SubscriptionTier.FREE);
            setDailyMessageCount(data.dailyMessageCount || 0);
          } else {
            console.log('User document not found or empty');
            setTier(SubscriptionTier.FREE);
          }
          
          setIsLoading(false);
        }, (error) => {
          console.error('Error in subscription listener:', error);
          setSubscriptionError('Failed to listen for subscription updates');
          setIsLoading(false);
          
          // Try to reconnect with exponential backoff
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
            console.log(`Retrying subscription listener in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
            
            setTimeout(() => {
              console.log(`Attempting to reconnect subscription listener (${retryCount}/${maxRetries})`);
              setupListener();
            }, delay);
          }
        });
      } catch (error) {
        console.error('Error setting up subscription listener:', error);
        setSubscriptionError('Failed to set up subscription listener');
        setIsLoading(false);
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        console.log('Unsubscribing from subscription listener');
        unsubscribe();
      }
    };
  }, [currentUser, refreshSubscription]);

  const incrementMessageCount = () => {
    if (!currentUser) return;
    setDailyMessageCount(prev => prev + 1);
  };

  const resetMessageCount = () => {
    if (!currentUser) return;
    setDailyMessageCount(0);
  };

  const checkCanPerformAction = (action: 'message' | 'bookmark' | 'search' | 'export') => {
    const limits = TIER_LIMITS[tier];

    switch (action) {
      case 'message':
        if (dailyMessageCount >= limits.dailyMessageLimit) {
          return {
            allowed: false,
            reason: `You've reached your daily message limit of ${limits.dailyMessageLimit}. Upgrade to Premium for unlimited messages.`
          };
        }
        break;
      case 'bookmark':
        // This would need to be checked against actual bookmark count in the database
        break;
      case 'search':
        if (!limits.canUseAdvancedSearch) {
          return {
            allowed: false,
            reason: 'Advanced search is a Premium feature. Upgrade to access advanced search capabilities.'
          };
        }
        break;
      case 'export':
        if (!limits.canExport) {
          return {
            allowed: false,
            reason: 'Exporting chat history is a Premium feature. Upgrade to enable exports.'
          };
        }
        break;
    }

    return { allowed: true };
  };

  const value = {
    tier,
    limits: TIER_LIMITS[tier],
    isLoading,
    dailyMessageCount,
    subscriptionError,
    incrementMessageCount,
    resetMessageCount,
    refreshSubscription,
    checkCanPerformAction,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 